import {
  InfiniteData,
  InfiniteQueryObserverLoadingErrorResult,
  InfiniteQueryObserverPendingResult,
  InfiniteQueryObserverRefetchErrorResult,
  InfiniteQueryObserverSuccessResult,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { Center } from "../ui/layout";
import { Heading } from "../ui/typography";
import { Spinner } from "../ui/Spinner";

type JSXElementOrNull = React.JSX.Element | null;

// Not sure why this is needed, it didn't used to be needed, but it is now.
//
// Conveniently, undefined is a disallowed value for `data` anyway, so this is
// safe, and will lint any queries that return undefined when they shouldn't.
type NonUndefined<T> = T extends undefined ? never : T;

type ErrorResult<TData, TError> =
  | InfiniteQueryObserverLoadingErrorResult<InfiniteData<TData>, TError>
  | InfiniteQueryObserverRefetchErrorResult<InfiniteData<TData>, TError>;

interface CreateInfiniteQueryHandlerOptions<TError> {
  /**
   * Default error handler
   */
  error: (query: ErrorResult<unknown, TError>) => JSXElementOrNull;
  /**
   * Default pending handler
   */
  pending: (query: InfiniteQueryObserverPendingResult<unknown, TError>) => JSXElementOrNull;
}

interface InfiniteQueryHandlerOptions<TData, TError> {
  query: UseInfiniteQueryResult<InfiniteData<TData>, TError>;
  /**
   * Optionally override default error handling
   */
  error?: (query: ErrorResult<TData, TError>) => JSXElementOrNull;
  /**
   * Optionally override pending state
   */
  pending?: (query: InfiniteQueryObserverPendingResult<InfiniteData<TData>, TError>) => JSXElementOrNull;
}

interface InfiniteQueryHandlerOptionsWithEmpty<TData, TError> extends InfiniteQueryHandlerOptions<TData, TError> {
  /**
   * Render callback for when the data is fetched successfully
   */
  success: (query: InfiniteQueryObserverSuccessResult<InfiniteData<TData>, TError>) => JSXElementOrNull;
  /**
   * Render callback for when the data is empty - when the `data` is `null`-ish or an empty array
   */
  empty: (query: InfiniteQueryObserverSuccessResult<InfiniteData<TData>, TError>) => JSXElementOrNull;
}
interface InfiniteQueryHandlerOptionsNoEmpty<TData, TError> extends InfiniteQueryHandlerOptions<TData, TError> {
  success: (query: InfiniteQueryObserverSuccessResult<InfiniteData<TData>, TError>) => JSXElementOrNull;
}

export function createInfiniteQueryHandler<TError>(queryCellOpts: CreateInfiniteQueryHandlerOptions<TError>) {
  function InfiniteQueryHandler<TData>(
    opts: InfiniteQueryHandlerOptionsWithEmpty<NonUndefined<TData>, TError>,
  ): JSXElementOrNull;
  function InfiniteQueryHandler<TData>(
    opts: InfiniteQueryHandlerOptionsNoEmpty<NonUndefined<TData>, TError>,
  ): JSXElementOrNull;
  function InfiniteQueryHandler<TData>(
    opts:
      | InfiniteQueryHandlerOptionsNoEmpty<NonUndefined<TData>, TError>
      | InfiniteQueryHandlerOptionsWithEmpty<NonUndefined<TData>, TError>,
  ) {
    const { query } = opts;

    if (query.status === "success" && !query.isPlaceholderData) {
      if (
        "empty" in opts &&
        (query.data == null ||
          query.data.pages == null ||
          (Array.isArray(query.data.pages) && query.data.pages.length === 0))
      ) {
        return opts.empty(query);
      }
      return opts.success(query);
    }

    if (query.status === "error") {
      return opts.error?.(query) ?? queryCellOpts.error(query);
    }
    if (query.status === "pending") {
      return opts.pending?.(query) ?? queryCellOpts.pending(query);
    }
    // impossible state
    return null;
  }
  return InfiniteQueryHandler;
}

export const InfiniteQueryHandler = createInfiniteQueryHandler({
  error: () => <Heading>Error loading</Heading>,
  pending: () => (
    <Center css={{ w: "100%", h: 64 }}>
      <Spinner />
    </Center>
  ),
});
